import '../styles/Experience.css';
import bdsLogoLight from '../assets/logos/BDS logo_landscape light.png';
import { Tooltip } from 'react-tooltip';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useApiCall } from '../hooks/useApiCall';
import { useAccount } from '../contexts/AccountContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { useShowroomTransition } from '../contexts/ShowroomTransitionContext';
import {
    RiArrowRightSLine,
    RiArrowLeftSLine,
    RiArrowLeftLine,
    RiHeartLine,
    RiHeartFill,
    RiHeart3Line,
    RiHeart3Fill,
    RiShoppingCartLine,
    RiSubtractLine,
    RiAddLine,
    RiCheckLine,
    RiErrorWarningLine,
    RiCloseLine,
    RiLoader4Line,
    RiDeleteBin6Line,
    RiArrowRightLine,
    RiTimerLine,
    RiTimerFlashLine
} from 'react-icons/ri';

// Pure function - no closures, no hooks - safe to call from any effect.
// Accepts dots array directly so it is decoupled from any module-level data ref.
const computeDotPositions = (dots, containerWidth, containerHeight) => {
    const videoAspectRatio = 1920 / 1080;
    const containerAspectRatio = containerWidth / containerHeight;
    return dots.map(dot => {
        let dotX, dotY;
        if (containerAspectRatio > videoAspectRatio) {
            const scale = containerWidth / 1920;
            const scaledVideoHeight = 1080 * scale;
            const cropTop = (scaledVideoHeight - containerHeight) / 2;
            dotX = (dot.x / 100) * containerWidth;
            dotY = (dot.y / 100) * scaledVideoHeight - cropTop;
        } else {
            const scale = containerHeight / 1080;
            const scaledVideoWidth = 1920 * scale;
            const cropLeft = (scaledVideoWidth - containerWidth) / 2;
            dotX = (dot.x / 100) * scaledVideoWidth - cropLeft;
            dotY = (dot.y / 100) * containerHeight;
        }
        return { x: dotX, y: dotY };
    });
};

function Experience() {

    const navigate = useNavigate();
    const { exitExperience } = useShowroomTransition();
    const location = useLocation();
    // Parse productId from path since Experience renders outside a <Route>
    const routeProductIdMatch = location.pathname.match(/^\/experience\/product\/(\d+)/);
    const routeProductId = routeProductIdMatch ? routeProductIdMatch[1] : undefined;
    const apiCall = useApiCall();
    const { account } = useAccount();
    const { refreshCounts, guestCart, guestWishlist, addToGuestCart, removeFromGuestCart, updateGuestCartQty, toggleGuestWishlist, isInGuestWishlist } = useCart();
    const { showSuccess, showError } = useToast();
    const videoRef = useRef(null);
    const overlayTimeoutRef = useRef(null);
    const autoAdvanceTimerRef = useRef(null);
    const travelStartRef = useRef(0);
    const travelEndRef = useRef(0);

    const nextTimestampRef = useRef(null);
    const prevTimestampRef = useRef(null);
    const [ sections, setSections ] = useState([]);
    const [ sectionsLoading, setSectionsLoading ] = useState(true);
    const [ sectionsError, setSectionsError ] = useState('');
    const [ currentSectionIndex, setCurrentSectionIndex ] = useState(0);
    const [ sectionFading, setSectionFading ] = useState(false);
    const sectionFadeTimerRef = useRef(null);
    const [ isPortrait, setIsPortrait ] = useState(() => window.innerHeight > window.innerWidth);

    useEffect(() => {
        const check = () => setIsPortrait(window.innerHeight > window.innerWidth);
        // orientationchange fires before the browser updates viewport dimensions on iOS/iPad;
        // defer by one frame so innerWidth/innerHeight reflect the new orientation.
        const checkDeferred = () => setTimeout(check, 100);
        window.addEventListener('resize', check);
        window.addEventListener('orientationchange', checkDeferred);
        return () => { window.removeEventListener('resize', check); window.removeEventListener('orientationchange', checkDeferred); };
    }, []);

    // Lock body scroll while the experience is mounted (prevents iOS rubber-band/scroll).
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
            document.documentElement.style.overflow = '';
        };
    }, []);

    const [ currentTimestampIndex, setCurrentTimestampIndex ] = useState(0);
    const [ videoInitialized, setVideoInitialized ] = useState(false);

    const currentSection = sections[currentSectionIndex] ?? null;
    const VIDEO_DATA = currentSection?.video ?? null;
    const CHAPTER_LABELS = currentSection?.chapterLabels ?? [];
    const hasShowroomContent = Boolean(VIDEO_DATA?.uri && Array.isArray(VIDEO_DATA?.timestamps) && VIDEO_DATA.timestamps.length > 0);
    const [ showDots, setShowDots ] = useState(false);
    const [ showOverlay, setShowOverlay ] = useState(true);
    const [ overlayFading, setOverlayFading ] = useState(false);
    const [ isAtTargetTimestamp, setIsAtTargetTimestamp ] = useState(false);
    const [ travelSegmentIndex, setTravelSegmentIndex ] = useState(null);
    const progressBarRef = useRef(null);
    const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(true);

    const [selectedDot, setSelectedDot] = useState(null);
    const [cardPosition, setCardPosition] = useState({ x: 0, y: 0, side: 'right' });
    const [wishlist, setWishlist] = useState([]);
    const [showChapterName, setShowChapterName] = useState(false);
    // { chapterIndex, positions[] } - positions are only valid for their chapter
    const [dotPixelPositions, setDotPixelPositions] = useState({ chapterIndex: -1, positions: [] });
    const skipperRef = useRef(null);
    const [skipperCorner, setSkipperCorner] = useState('top-right'); // top-right | bottom-right | top-left | bottom-left

    // Product drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerProduct, setDrawerProduct] = useState(null);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [videoFullyLoaded, setVideoFullyLoaded] = useState(false);
    const [bufferSpeed, setBufferSpeed] = useState('');
    const bufferSpeedRef = useRef({ prevBytes: 0, prevTime: 0 });
    const videoFileSizeRef = useRef(0);
    const bufferBarRef = useRef(null);
    const bufferPctTextRef = useRef(null);
    const bufferingTimerRef = useRef(null);
    const pendingSeekRef = useRef(false);
    const sectionSwitchingRef = useRef(false);
    const tiltRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });
    const tiltRafRef = useRef(null);
    const tiltWrapperRef = useRef(null);
    const isAtTargetTimestampRef = useRef(false);
    const [drawerImageIndex, setDrawerImageIndex] = useState(0);
    const [drawerQuantity, setDrawerQuantity] = useState(1);
    const [drawerModifiers, setDrawerModifiers] = useState({});
    const [drawerWishlisted, setDrawerWishlisted] = useState(false);
    const [drawerAddingToCart, setDrawerAddingToCart] = useState(false);
    const [drawerAddedToCart, setDrawerAddedToCart] = useState(false);
    const [drawerTogglingWishlist, setDrawerTogglingWishlist] = useState(false);
    const [experienceCartOpen, setExperienceCartOpen] = useState(false);
    const [experienceCartLoading, setExperienceCartLoading] = useState(false);
    const [experienceCartItems, setExperienceCartItems] = useState([]);
    const [experienceCartUpdating, setExperienceCartUpdating] = useState({});

    // ── Onboarding guide ────────────────────────────────────────────
    const GUIDE_STEPS = [
        {
            id: 'sections',
            title: 'Sections',
            body: 'Switch between different areas of the showroom using the tabs at the top.',
            anchor: 'top-center',
            tip: 'top',
        },
        {
            id: 'navigate',
            title: 'Next chapter',
            body: 'Tap the right arrow - or press → on your keyboard - to advance to the next chapter.',
            anchor: 'right-center',
            tip: 'right',
        },
        {
            id: 'dots',
            title: 'Shop the scene',
            body: 'See those glowing dots? Each marks a product in the scene - tap to explore and add to cart.',
            anchor: 'center-upper',
            tip: 'bottom',
        },
        {
            id: 'strip',
            title: 'All products here',
            body: 'The panel on the left shows every product in this chapter. Tap any thumbnail to explore.',
            anchor: 'above-strip',
            tip: 'bottom',
        },
    ];
    const [guideStep, setGuideStep] = useState(() =>
        localStorage.getItem('bds_exp_guide_nav') ? -1 : 0
    );
    const isGuideActive = guideStep >= 0 && guideStep < GUIDE_STEPS.length;
    const isGuideActiveRef = useRef(false);
    isGuideActiveRef.current = isGuideActive;
    const advanceGuide = () => {
        if (guideStep === 1) {
            // Nav guide complete - hide until the video pauses for shop steps
            localStorage.setItem('bds_exp_guide_nav', '1');
            setGuideStep(-1);
        } else if (guideStep >= GUIDE_STEPS.length - 1) {
            localStorage.setItem('bds_exp_guide_shop', '1');
            setGuideStep(-1);
        } else {
            setGuideStep(guideStep + 1);
        }
    };
    const dismissGuide = () => {
        localStorage.setItem('bds_exp_guide_nav', '1');
        localStorage.setItem('bds_exp_guide_shop', '1');
        setGuideStep(-1);
    };
    // Trigger shop guide (steps 2-3) the first time the video pauses at a chapter
    const shopGuideTriggerRef = useRef(false);
    // { vx, vy } - viewport-centre of the first dot, read after the dots step mounts
    const [dotGuidePos, setDotGuidePos] = useState(null);
    const [stripGuidePos, setStripGuidePos] = useState(null);
    // Dev helper: window.__resetGuide() resets the guide from the browser console
    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') return;
        window.__resetGuide = () => {
            localStorage.removeItem('bds_exp_guide_nav');
            localStorage.removeItem('bds_exp_guide_shop');
            shopGuideTriggerRef.current = false;
            setGuideStep(0);
            console.log('[Guide] reset - guide will show from step 1');
        };
        return () => { delete window.__resetGuide; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        // When dotPixelPositions arrive (React has committed them to DOM, so
        // getBoundingClientRect is accurate) trigger the shop guide if conditions met.
        if (!isAtTargetTimestamp || shopGuideTriggerRef.current || localStorage.getItem('bds_exp_guide_shop')) return;
        if (dotPixelPositions.positions.length === 0) return;
        const dots = VIDEO_DATA?.timestamps?.[currentTimestampIndex]?.dots;
        if (!dots?.length) return;
        shopGuideTriggerRef.current = true;
        const el = document.querySelector('.VideoDot');
        if (el) {
            const r = el.getBoundingClientRect();
            setDotGuidePos({ vx: r.left + r.width / 2, vy: r.top + r.height / 2 });
        }
        setGuideStep(2);
    }, [dotPixelPositions]); // eslint-disable-line react-hooks/exhaustive-deps

    // Measure the strip element when guide reaches the strip step
    useEffect(() => {
        if (guideStep !== 3) { setStripGuidePos(null); return; }
        const el = document.querySelector('.SceneShopStrip');
        if (!el) return;
        const r = el.getBoundingClientRect();
        setStripGuidePos({ left: r.left, top: r.top, width: r.width, height: r.height });
    }, [guideStep]);

    // Pause video while the guide is active; resume when guide closes (if mid-travel)
    const guidePausedVideoRef = useRef(false);
    useEffect(() => {
        if (!videoRef.current) return;
        if (isGuideActive) {
            guidePausedVideoRef.current = !videoRef.current.paused;
            videoRef.current.pause();
        } else {
            if (guidePausedVideoRef.current) {
                guidePausedVideoRef.current = false;
                // Only resume if we were mid-travel (not parked at a chapter)
                if (!isAtTargetTimestamp && videoInitialized) {
                    videoRef.current.play().catch(() => {});
                }
            }
        }
    }, [isGuideActive]); // eslint-disable-line react-hooks/exhaustive-deps

    const wishlistSet = useMemo(() => new Set(wishlist.map(w => w.title)), [wishlist]);
    const wishlistProductIds = useMemo(() => new Set(wishlist.filter(w => w.productId).map(w => w.productId)), [wishlist]);
    const isWishlisted = useCallback((item) => {
        if (item.productId) return wishlistProductIds.has(item.productId);
        return wishlistSet.has(item.title);
    }, [wishlistSet, wishlistProductIds]);

    // Sync guestWishlist → local wishlist state when no account
    useEffect(() => {
        if (account?.token) return;
        setWishlist(guestWishlist.map(w => ({ productId: w.product_id, title: w.title, image: w.image })));
    }, [guestWishlist, account]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync guestCart → in-experience cart panel when no account
    useEffect(() => {
        if (account?.token) return;
        setExperienceCartItems(guestCart.map(item => ({
            cart_id: `guest_${item.product_id}`,
            product_id: item.product_id,
            qty: item.qty,
            name: item.name,
            primary_image: item.image,
            price: item.price,
            price_sale: item.price_sale,
            price_curr: item.price_curr,
        })));
    }, [guestCart, account]); // eslint-disable-line react-hooks/exhaustive-deps


    // Intercept browser back button - show exit transition before leaving
    useEffect(() => {
        // Push a sentinel entry so there's something to pop into
        window.history.pushState({ experienceSentinel: true }, '');
        const onPopState = (e) => {
            if (e.state?.experienceSentinel) return; // popped our own sentinel, ignore
            exitExperience(); // no arg - App.js will use the saved path
        };
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear travel indicator when arriving at a chapter
    useEffect(() => {
        if (isAtTargetTimestamp) {
            setTravelSegmentIndex(null);
            if (progressBarRef.current) progressBarRef.current.style.width = '0%';
        }
    }, [isAtTargetTimestamp]);

    // RAF loop: update travel progress while video plays between chapters
    // Drives the progress bar via a DOM ref - no React state, no re-renders, perfectly smooth.
    useEffect(() => {
        if (isAtTargetTimestamp || travelSegmentIndex === null) return;
        let rafId;
        const update = () => {
            const ct = videoRef.current?.currentTime || 0;
            const duration = travelEndRef.current - travelStartRef.current;
            if (duration > 0 && progressBarRef.current) {
                const pct = Math.min(100, Math.max(0, ((ct - travelStartRef.current) / duration) * 100));
                progressBarRef.current.style.width = pct + '%';
            }
            rafId = requestAnimationFrame(update);
        };
        rafId = requestAnimationFrame(update);
        return () => cancelAnimationFrame(rafId);
    }, [isAtTargetTimestamp, travelSegmentIndex]);

    // Compute dot pixel positions AFTER layout is committed, so DOM reads are accurate
    useEffect(() => {
        if (!hasShowroomContent) {
            setDotPixelPositions({ chapterIndex: -1, positions: [] });
            return;
        }
        if (!showDots) {
            setDotPixelPositions({ chapterIndex: -1, positions: [] });
            return;
        }
        const capturedChapter = currentTimestampIndex;
        const dots = VIDEO_DATA.timestamps[capturedChapter]?.dots || [];
        const raf = requestAnimationFrame(() => {
            const containerWidth = videoRef.current?.parentElement?.clientWidth || window.innerWidth;
            const containerHeight = videoRef.current?.parentElement?.clientHeight || window.innerHeight;
            setDotPixelPositions({
                chapterIndex: capturedChapter,
                positions: computeDotPositions(dots, containerWidth, containerHeight)
            });
        });
        return () => cancelAnimationFrame(raf);
    }, [showDots, currentTimestampIndex, currentSectionIndex, hasShowroomContent]); // eslint-disable-line react-hooks/exhaustive-deps

    // Recompute dot positions on window resize
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        const handleResize = () => {
            const dots = VIDEO_DATA.timestamps[currentTimestampIndex]?.dots || [];
            const containerWidth = videoRef.current?.parentElement?.clientWidth || window.innerWidth;
            const containerHeight = videoRef.current?.parentElement?.clientHeight || window.innerHeight;
            requestAnimationFrame(() => {
                setDotPixelPositions({
                    chapterIndex: currentTimestampIndex,
                    positions: computeDotPositions(dots, containerWidth, containerHeight)
                });
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [currentTimestampIndex, currentSectionIndex, hasShowroomContent]); // eslint-disable-line react-hooks/exhaustive-deps

    // Video navigation functions
    const nextTimestamp = () => {
        if (!hasShowroomContent) return;
        setSelectedDot(null);
        if (videoRef.current && isAtTargetTimestamp && videoRef.current.paused) {
            if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
                overlayTimeoutRef.current = null;
            }
            setShowDots(false);
            setShowOverlay(true);
            setOverlayFading(false);
            setIsAtTargetTimestamp(false);
            if (currentTimestampIndex < VIDEO_DATA.timestamps.length - 1) {
                const newIndex = currentTimestampIndex + 1;
                setTravelSegmentIndex(currentTimestampIndex); // travelling segment between ch i and i+1
                setCurrentTimestampIndex(newIndex);
                const startTime = videoRef.current.currentTime; // resume from exact pause point - no jump
                travelStartRef.current = startTime;
                travelEndRef.current = VIDEO_DATA.timestamps[newIndex].seconds;
                videoRef.current.playbackRate = 1; // normal cinematic speed chapter-to-chapter
                videoRef.current.play().catch(() => {});
            } else {
                videoRef.current.playbackRate = 1;
                videoRef.current.play().catch(() => {});
            }
        } else {
            const currentTime = videoRef.current?.currentTime || 0;
            const currentTimestamp = VIDEO_DATA.timestamps[currentTimestampIndex];
            if (currentTime < currentTimestamp.seconds) {
                // Still in intro - fast-forward to the chapter instead of instant seek.
                // The RAF stop-watcher is already running and will halt at the target.
                setShowDots(false);
                setShowOverlay(true);
                setOverlayFading(false);
                videoRef.current.playbackRate = 8;
                if (videoRef.current.paused) videoRef.current.play().catch(() => {});
            } else if (currentTimestampIndex < VIDEO_DATA.timestamps.length - 1) {
                const newIndex = currentTimestampIndex + 1;
                pendingSeekRef.current = true;
                setShowOverlay(true);
                setOverlayFading(false);
                setShowDots(false);
                videoRef.current.currentTime = VIDEO_DATA.timestamps[newIndex].seconds;
                videoRef.current.pause();
                setCurrentTimestampIndex(newIndex);
            } else {
                const ts = VIDEO_DATA.timestamps[currentTimestampIndex];
                if (Math.abs((videoRef.current?.currentTime || 0) - ts.seconds) < 0.5) {
                    videoRef.current.currentTime = ts.seconds + 1;
                }
                videoRef.current.play().catch(() => {});
            }
        }
    };

    const prevTimestamp = () => {
        if (!hasShowroomContent) return;
        setSelectedDot(null);
        if (currentTimestampIndex === 0) return;
        if (overlayTimeoutRef.current) {
            clearTimeout(overlayTimeoutRef.current);
            overlayTimeoutRef.current = null;
        }
        const prevIndex = currentTimestampIndex - 1;
        pendingSeekRef.current = true;
        videoRef.current.currentTime = VIDEO_DATA.timestamps[prevIndex].seconds;
        videoRef.current.pause();
        setCurrentTimestampIndex(prevIndex);
        setIsAtTargetTimestamp(false);
        setShowDots(false);
        setShowOverlay(true);
        setOverlayFading(false);
        setTravelSegmentIndex(null);
        // handleSeeked will set isAtTargetTimestamp + showDots once seek is confirmed
    };

    
    // Video event handlers
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        const videoElement = videoRef.current;
        if (!videoElement) return;

        // RAF loop: monitors currentTime tightly (~16ms) and stops the video
        // exactly at the target chapter - avoids the timeupdate overshoot/snap-back flash.
        let stopRafId = null;

        const startStopWatcher = () => {
            if (stopRafId) return;
            const watch = () => {
                if (videoElement.paused || isAtTargetTimestamp) { stopRafId = null; return; }
                const ct = videoElement.currentTime;
                const target = VIDEO_DATA.timestamps[currentTimestampIndex].seconds;
                // Dynamic lookahead: at fast playback rates the video advances much more
                // per RAF frame (~16ms), so we need proportionally more runway to stop
                // cleanly before overshooting and snapping back.
                const rate = videoElement.playbackRate || 1;
                const lookahead = (1 / 60) * Math.max(1, rate);
                if (ct >= target - lookahead) {
                    videoElement.pause();
                    videoElement.currentTime = target;
                    videoElement.playbackRate = 1; // restore after fast-forward
                    setShowDots(true);
                    setIsAtTargetTimestamp(true);
                    setOverlayFading(true);
                    if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
                    overlayTimeoutRef.current = setTimeout(() => {
                        setShowOverlay(false);
                        setOverlayFading(false);
                    }, 1500);
                    stopRafId = null;
                    return;
                }
                stopRafId = requestAnimationFrame(watch);
            };
            stopRafId = requestAnimationFrame(watch);
        };

        const handlePlay = () => {
            sectionSwitchingRef.current = false;
            if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
                overlayTimeoutRef.current = null;
            }
            setShowDots(false);
            setIsAtTargetTimestamp(false);
            setShowOverlay(true);
            setOverlayFading(false);
            startStopWatcher();
        };

        const handlePause = () => {
            if (stopRafId) { cancelAnimationFrame(stopRafId); stopRafId = null; }
            // Ignore pauses during section switches or buffering
            if (sectionSwitchingRef.current) return;
            // Ignore pause events fired during buffering - not a real chapter arrival
            if (videoElement.readyState < 4) return;
            const currentTime = videoElement.currentTime;
            const currentTimestamp = VIDEO_DATA.timestamps[currentTimestampIndex];
            if (Math.abs(currentTime - currentTimestamp.seconds) < 0.5) {
                videoElement.playbackRate = 1; // restore in case paused mid fast-forward
                setShowDots(true);
                setIsAtTargetTimestamp(true);
                setOverlayFading(true);
                if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
                overlayTimeoutRef.current = setTimeout(() => {
                    setShowOverlay(false);
                    setOverlayFading(false);
                }, 1500);
            }
        };

        const handleLoadedData = () => {
            if (!videoInitialized) {
                videoElement.currentTime = 0;
                setShowOverlay(true);
                setOverlayFading(false);
                // Don't start playing if the onboarding guide is showing
                if (!isGuideActiveRef.current) {
                    videoElement.play().catch(() => {});
                }
                setVideoInitialized(true);
            }
        };

        // If video is already playing when this effect re-runs, restart the watcher
        if (!videoElement.paused && !isAtTargetTimestamp) startStopWatcher();

        const handleSeeked = () => {
            if (!pendingSeekRef.current) return;
            pendingSeekRef.current = false;
            setIsAtTargetTimestamp(true);
            setShowDots(true);
            setOverlayFading(true);
            if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
            overlayTimeoutRef.current = setTimeout(() => {
                setShowOverlay(false);
                setOverlayFading(false);
            }, 1500);
        };

        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);
        videoElement.addEventListener('loadeddata', handleLoadedData);
        videoElement.addEventListener('seeked', handleSeeked);

        return () => {
            if (stopRafId) { cancelAnimationFrame(stopRafId); stopRafId = null; }
            // Clear timeout on cleanup
            if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
            }
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
            videoElement.removeEventListener('loadeddata', handleLoadedData);
            videoElement.removeEventListener('seeked', handleSeeked);
        };
    }, [currentTimestampIndex, videoInitialized, isAtTargetTimestamp, hasShowroomContent, VIDEO_DATA?.timestamps]);

    // Pause video when navigating away (component unmounts)
    useEffect(() => {
        return () => {
            if (videoRef.current && !videoRef.current.paused) {
                videoRef.current.pause();
            }
        };
    }, []);

    // When section switches, reload the new video src. Skip the very first run -
    // the browser already starts fetching as soon as the <video src=…> is in the DOM,
    // and calling .load() at that point aborts the in-progress download and wastes cache.
    const sectionLoadedOnceRef = useRef(false);
    useEffect(() => {
        if (!hasShowroomContent || !videoRef.current) return;
        if (!sectionLoadedOnceRef.current) {
            sectionLoadedOnceRef.current = true;
            return; // let the browser's natural preload do its job
        }
        videoRef.current.load();
    }, [currentSectionIndex, hasShowroomContent]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load sections from API only.
    useEffect(() => {
        let cancelled = false;
        apiCall('showroom/sections', 'GET')
            .then(res => {
                if (cancelled) return;
                if (res?.success && Array.isArray(res.data?.sections) && res.data.sections.length > 0) {
                    setSections(res.data.sections);
                    setSectionsError('');
                    // Inject a preload hint so the browser starts fetching the video
                    // immediately - before the <video> element even renders.
                    const firstVideoUri = res.data.sections[0]?.video?.uri;
                    if (firstVideoUri) {
                        const existing = document.querySelector(`link[rel="preload"][data-exp-video]`);
                        if (!existing) {
                            const link = document.createElement('link');
                            link.rel = 'preload';
                            link.as = 'video';
                            link.href = firstVideoUri;
                            link.setAttribute('data-exp-video', '1');
                            document.head.appendChild(link);
                        }
                        // HEAD request fired immediately so videoFileSizeRef is ready before canplaythrough
                        fetch(firstVideoUri, { method: 'HEAD' })
                            .then(r => {
                                const cl = r.headers.get('content-length');
                                if (cl) videoFileSizeRef.current = parseInt(cl, 10);
                            })
                            .catch(() => {});
                    }
                    // Prefetch remaining section videos at low priority using <link rel="prefetch">
                    // so they're already in cache when the user switches sections.
                    res.data.sections.slice(1).forEach(section => {
                        const uri = section?.video?.uri;
                        if (!uri) return;
                        if (document.querySelector(`link[rel="prefetch"][href="${uri}"]`)) return;
                        const link = document.createElement('link');
                        link.rel = 'prefetch';
                        link.as = 'video';
                        link.href = uri;
                        document.head.appendChild(link);
                    });
                } else {
                    setSections([]);
                    setSectionsError(res?.response || 'No showroom sections are available yet.');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSections([]);
                    setSectionsError('Unable to load the showroom right now.');
                }
            })
            .finally(() => { if (!cancelled) setSectionsLoading(false); });
        return () => {
            cancelled = true;
            // Remove the preload hint when the experience unmounts
            document.querySelector('link[rel="preload"][data-exp-video]')?.remove();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // (handleLoadedData inside the video-event effect handles play() on first load)

    const handleDotClick = (dot, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const cardWidth = 450;
        const dotX = rect.left + rect.width / 2;
        const y = window.innerHeight / 2;
        
        // Simple left/right logic based on available space
        let side = 'right';
        let x = dotX + 50; // Default to right side
        
        // If right side would go off screen, use left side
        if (dotX + 50 + cardWidth > viewportWidth - 20) {
            side = 'left';
            x = dotX - 50;
        }
        
        // Set position first, then show card
        setCardPosition({ x, y, side });
        setSelectedDot(dot);
    };

    const closePopup = () => {
        setSelectedDot(null);
    };

    const jumpToTimestamp = (index) => {
        if (!hasShowroomContent || !videoRef.current) return;
        if (!videoRef.current) return;
        videoRef.current.currentTime = VIDEO_DATA.timestamps[index].seconds;
        videoRef.current.pause();
        setCurrentTimestampIndex(index);
        setIsAtTargetTimestamp(true);
        setShowDots(true);
        setSelectedDot(null);
    };

    const switchSection = (index) => {
        if (index === currentSectionIndex) return;
        if (sectionFadeTimerRef.current) clearTimeout(sectionFadeTimerRef.current);
        setSectionFading(true);
        sectionFadeTimerRef.current = setTimeout(() => {
        // Reset all video state for the new section
        sectionSwitchingRef.current = true;
        pendingSeekRef.current = false;
        setCurrentSectionIndex(index);
        setCurrentTimestampIndex(0);
        setVideoInitialized(false);
        setVideoFullyLoaded(false);
        setIsBuffering(true);
        setShowDots(false);
        setSelectedDot(null);
        setIsAtTargetTimestamp(false);
        setShowOverlay(true);
        setOverlayFading(false);
        if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
        if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
        // Video src will update on next render via VIDEO_DATA; restart in the init effect
        setSectionFading(false);
        }, 280);
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    };

    const toggleWishlist = async (item) => {
        if (!account?.token) {
            if (item.productId) {
                const isIn = wishlistProductIds.has(item.productId);
                toggleGuestWishlist({ product_id: item.productId, name: item.title, image: item.image || null, price: String(item.price || 0), price_curr: item.price_curr || 'SRD' });
                setWishlist(prev => isIn
                    ? prev.filter(w => w.productId !== item.productId)
                    : [...prev, item]);
            } else {
                setWishlist(prev => {
                    const isIn = prev.some(w => w.title === item.title);
                    return isIn ? prev.filter(w => w.title !== item.title) : [...prev, item];
                });
            }
            return;
        }
        if (item.productId) {
            const isIn = wishlistProductIds.has(item.productId);
            // Optimistic update
            setWishlist(prev => isIn
                ? prev.filter(w => w.productId !== item.productId)
                : [...prev, item]);
            const endpoint = isIn ? 'account/wishlist/remove' : 'account/wishlist/add';
            try {
                const res = await apiCall(endpoint, 'POST', { product_id: item.productId }, account);
                if (res?.success) {
                    refreshCounts();
                } else {
                    // Revert
                    setWishlist(prev => isIn ? [...prev, item] : prev.filter(w => w.productId !== item.productId));
                    showError(res?.response || 'Failed to update wishlist');
                }
            } catch {
                setWishlist(prev => isIn ? [...prev, item] : prev.filter(w => w.productId !== item.productId));
                showError('Failed to update wishlist');
            }
        } else {
            // Local-only toggle for static items without a real product link
            setWishlist(prev => {
                const isIn = prev.some(w => w.title === item.title);
                return isIn ? prev.filter(w => w.title !== item.title) : [...prev, item];
            });
        }
    };

    // ── Product Drawer ──────────────────────────────────────────────────────────

    const openProductDrawer = (productId, updateHistory = true) => {
        setDrawerOpen(true);
        setDrawerProduct(null);
        setDrawerLoading(true);
        setDrawerImageIndex(0);
        setDrawerQuantity(1);
        setDrawerModifiers({});
        setDrawerWishlisted(false);
        setDrawerAddingToCart(false);
        setDrawerAddedToCart(false);
        setDrawerTogglingWishlist(false);
        setExperienceCartUpdating({});
        if (updateHistory) {
            navigate(`/experience/product/${productId}`, { replace: true });
        }

        apiCall('product/get', 'POST', { product_id: parseInt(productId) }).then(res => {
            if (res?.success && res.data?.product) {
                setDrawerProduct(res.data.product);
                // Check wishlist status
                if (account?.token) {
                    apiCall('account/wishlist/check', 'POST', { product_id: parseInt(productId) }, account).then(r => {
                        if (r?.success) setDrawerWishlisted(r.data.in_wishlist);
                    }).catch(() => {});
                } else {
                    setDrawerWishlisted(isInGuestWishlist(parseInt(productId)));
                }
            }
            setDrawerLoading(false);
        }).catch(() => setDrawerLoading(false));
    };

    const closeProductDrawer = () => {
        setDrawerOpen(false);
        setTimeout(() => setDrawerProduct(null), 350);
        navigate('/experience', { replace: true });
    };

    // Sync drawer open/closed state with URL path changes
    useEffect(() => {
        if (routeProductId) {
            // URL has a product ID - open drawer if not already showing this product
            if (!drawerOpen || drawerProduct?.id !== parseInt(routeProductId)) {
                openProductDrawer(parseInt(routeProductId), false);
            }
        } else if (drawerOpen && location.pathname === '/experience') {
            // Back-navigated to /experience - close drawer
            setDrawerOpen(false);
            setTimeout(() => setDrawerProduct(null), 350);
        }
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchDrawerCart = useCallback(async () => {
        if (!account?.token) return; // guestCart effect manages experience cart for guests

        setExperienceCartLoading(true);
        try {
            const response = await apiCall('account/cart/get', 'POST', {});
            if (response?.success) {
                setExperienceCartItems(response.data?.cart || []);
            } else {
                showError(response?.response || response?.message || 'Failed to load cart');
            }
        } catch {
            showError('Failed to load cart');
        } finally {
            setExperienceCartLoading(false);
        }
    }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (account?.token) {
            fetchDrawerCart();
            return;
        }
        // guestCart sync effect handles populating experienceCartItems for guests.
        // Only close the cart panel if the user is not signed in.
        setExperienceCartOpen(false);
    }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

    const getDrawerCartItemPrice = (item) => {
        const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
        return hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
    };

    const getDrawerCartItemTotal = (item) => {
        const qty = parseInt(item.qty, 10) || 0;
        // Modifier amount replaces base price (versions/variants)
        let unitPrice = getDrawerCartItemPrice(item);
        if (item.options) {
            try {
                const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                if (options?.modifiers && Array.isArray(options.modifiers)) {
                    const priceModifier = options.modifiers.find(m => m.option?.amount && parseFloat(m.option.amount) > 0);
                    if (priceModifier) unitPrice = parseFloat(priceModifier.option.amount);
                }
            } catch {
                return unitPrice * qty;
            }
        }
        return unitPrice * qty;
    };

    const getDrawerCartCurrency = () => experienceCartItems[0]?.price_curr || drawerProduct?.price?.curr || '$';

    const getDrawerCartTotal = () => experienceCartItems.reduce((sum, item) => sum + getDrawerCartItemTotal(item), 0);

    const toggleExperienceCart = async () => {
        const nextOpen = !experienceCartOpen;
        setExperienceCartOpen(nextOpen);
        if (nextOpen) {
            await fetchDrawerCart();
        }
    };

    const openExperienceCart = async () => {
        if (drawerOpen) {
            setDrawerOpen(false);
            setTimeout(() => setDrawerProduct(null), 350);
            navigate('/experience', { replace: true });
        }
        setExperienceCartOpen(true);
        await fetchDrawerCart();
    };

    const updateDrawerCartQuantity = async (cartId, nextQty) => {
        if (!account?.token) {
            if (String(cartId).startsWith('guest_')) {
                updateGuestCartQty(parseInt(String(cartId).replace('guest_', '')), nextQty);
            }
            return;
        }
        if (nextQty < 1) return;
        setExperienceCartUpdating(prev => ({ ...prev, [cartId]: true }));
        try {
            const response = await apiCall('account/cart/update', 'POST', { cart_id: cartId, qty: nextQty }, account, null);
            if (response?.success) {
                setExperienceCartItems(prev => prev.map(item => item.cart_id === cartId ? { ...item, qty: nextQty } : item));
                refreshCounts();
            } else {
                showError(response?.response || response?.message || 'Failed to update cart');
            }
        } catch {
            showError('Failed to update cart');
        } finally {
            setExperienceCartUpdating(prev => ({ ...prev, [cartId]: false }));
        }
    };

    const removeDrawerCartItem = async (cartId) => {
        if (!account?.token) {
            if (String(cartId).startsWith('guest_')) {
                removeFromGuestCart(parseInt(String(cartId).replace('guest_', '')));
            }
            return;
        }
        setExperienceCartUpdating(prev => ({ ...prev, [cartId]: true }));
        try {
            const response = await apiCall('account/cart/remove', 'POST', { cart_id: cartId }, account, null);
            if (response?.success) {
                setExperienceCartItems(prev => prev.filter(item => item.cart_id !== cartId));
                refreshCounts();
            } else {
                showError(response?.response || response?.message || 'Failed to remove item');
            }
        } catch {
            showError('Failed to remove item');
        } finally {
            setExperienceCartUpdating(prev => ({ ...prev, [cartId]: false }));
        }
    };

    const getDrawerTransformed = () => {
        if (!drawerProduct) return null;
        const p = drawerProduct;
        return {
            id: p.id,
            name: p.name,
            shortDescription: p.description ? p.description.substring(0, 120) + '...' : '',
            description: p.description || '',
            comingSoon: p.coming_soon || false,
            price: {
                curr: p.price?.curr || '$',
                amount: p.sale_price && p.sale_price > 0 ? p.sale_price : p.price?.amount || 0,
                originalAmount: p.sale_price && p.sale_price > 0 ? p.price?.amount : null,
                discount: p.sale_price && p.sale_price > 0 ?
                    Math.round(((p.price?.amount - p.sale_price) / p.price?.amount) * 100) : 0
            },
            images: p.images && p.images.length > 0 ? p.images : [{ id: 1, url: '/data/images/placeholder.jpg', alt: p.name }],
            modifiers: p.modifiers && typeof p.modifiers === 'object' ?
                Object.entries(p.modifiers).map(([key, value]) => ({
                    id: key.toLowerCase().replace(/\s+/g, '_'),
                    name: key,
                    required: false,
                    options: Array.isArray(value) ? value.map(opt => ({
                        id: `${key.toLowerCase()}_${opt.id}`,
                        name: opt.name,
                        description: opt.description || '',
                        amount: opt.amount || 0,
                        inStock: true
                    })) : []
                })) : [],
            features: p.features ? p.features.split('\n').filter(f => f.trim()) : [],
            inStock: p.in_stock || false,
            stockCount: p.stock || 0,
            brand: p.brand || '',
        };
    };

    const calculateDrawerTotal = (tp) => {
        if (!tp) return 0;
        // Modifier amount replaces base price (versions/variants)
        const modifierAmounts = Object.values(drawerModifiers).filter(opt => opt?.amount > 0);
        const unitPrice = modifierAmounts.length > 0 ? modifierAmounts[0].amount : tp.price.amount;
        return unitPrice * drawerQuantity;
    };

    const handleDrawerAddToCart = async (tp) => {
        if (tp.comingSoon) { showError('This product is coming soon'); return; }
        if (!tp.inStock) { showError('This product is currently out of stock'); return; }

        const requiredMissing = tp.modifiers.filter(m => m.required && !drawerModifiers[m.id]);
        if (requiredMissing.length > 0) { showError(`Please select: ${requiredMissing.map(m => m.name).join(', ')}`); return; }

        const mods = Object.entries(drawerModifiers).map(([mid, opt]) => {
            const mod = tp.modifiers.find(m => m.id === mid);
            return { name: mod.name, option: { item: opt.name, amount: opt.amount || 0 } };
        });
        const options = mods.length > 0 ? JSON.stringify({ modifiers: mods }) : null;

        if (!account?.token) {
            addToGuestCart({
                product_id: tp.id,
                qty: drawerQuantity,
                options,
                name: tp.name,
                image: tp.images?.[0]?.url || null,
                price: String(tp.price?.amount || 0),
                price_sale: null,
                price_curr: tp.price?.curr || 'SRD',
            });
            setDrawerAddedToCart(true);
            showSuccess('Added to cart');
            refreshCounts();
            return;
        }

        setDrawerAddingToCart(true);
        try {
            const res = await apiCall('account/cart/add', 'POST', { product_id: tp.id, qty: drawerQuantity, options }, account);
            if (res?.success) {
                setDrawerAddedToCart(true);
                refreshCounts();
                fetchDrawerCart();
            } else {
                showError(res?.response || 'Failed to add product to cart');
            }
        } catch { showError('Failed to add product to cart'); }
        finally { setDrawerAddingToCart(false); }
    };

    const handleDrawerWishlistToggle = async (tp) => {
        if (!account?.token) {
            toggleGuestWishlist({ product_id: tp.id, name: tp.name, image: tp.images?.[0]?.url || null, price: String(tp.price?.amount || 0), price_curr: tp.price?.curr || 'SRD' });
            setDrawerWishlisted(v => !v);
            showSuccess(drawerWishlisted ? 'Removed from wishlist' : 'Added to wishlist');
            refreshCounts();
            return;
        }
        setDrawerTogglingWishlist(true);
        try {
            const endpoint = drawerWishlisted ? 'account/wishlist/remove' : 'account/wishlist/add';
            const body = { product_id: tp.id };
            if (!drawerWishlisted) {
                const mods = Object.entries(drawerModifiers).map(([mid, opt]) => {
                    const mod = tp.modifiers.find(m => m.id === mid);
                    return { name: mod.name, option: { item: opt.name, amount: opt.amount || 0 } };
                });
                if (mods.length > 0) body.options = JSON.stringify({ modifiers: mods });
            }
            const res = await apiCall(endpoint, 'POST', body, account);
            if (res?.success) {
                setDrawerWishlisted(!drawerWishlisted);
                showSuccess(drawerWishlisted ? 'Removed from wishlist' : 'Added to wishlist', 3000, () => navigate('/wishlist'));
                refreshCounts();
            } else { showError(res?.response || 'Failed to update wishlist'); }
        } catch { showError('Failed to update wishlist'); }
        finally { setDrawerTogglingWishlist(false); }
    };

    // Sync refs in render body - cheaper than two effects that run every render
    nextTimestampRef.current = nextTimestamp;
    prevTimestampRef.current = prevTimestamp;

    // Keyboard navigation
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        const handleKey = (e) => {
            if (e.key === 'ArrowRight') nextTimestampRef.current?.();
            else if (e.key === 'ArrowLeft') prevTimestampRef.current?.();
            else if (e.key === 'Escape') setSelectedDot(null);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [hasShowroomContent]);

    // Auto-advance: after 10s paused at a chapter with no card open, play to next
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        if (!autoAdvanceEnabled || !isAtTargetTimestamp || selectedDot || isGuideActive || currentTimestampIndex >= VIDEO_DATA.timestamps.length - 1 || isBuffering || experienceCartOpen || drawerOpen) {
            return;
        }
        const timer = setTimeout(() => {
            if (!videoRef.current) return;
            const newIndex = currentTimestampIndex + 1;
            const startT = videoRef.current.currentTime; // resume from exact pause point - no jump
            travelStartRef.current = startT;
            travelEndRef.current = VIDEO_DATA.timestamps[newIndex].seconds;
            setCurrentTimestampIndex(newIndex);
            setTravelSegmentIndex(currentTimestampIndex);
            setShowDots(false);
            setShowOverlay(true);
            setOverlayFading(false);
            setIsAtTargetTimestamp(false);
            setSelectedDot(null);
            videoRef.current.playbackRate = 1; // auto-advance plays at normal speed
            videoRef.current.play().catch(() => {});
        }, 10 * 1000);
        autoAdvanceTimerRef.current = timer;
        return () => clearTimeout(timer);
    }, [isAtTargetTimestamp, selectedDot, isGuideActive, currentTimestampIndex, hasShowroomContent, autoAdvanceEnabled, isBuffering, experienceCartOpen, drawerOpen, VIDEO_DATA?.timestamps]);

    // Chapter name toast: show for 2.5s each time we arrive at a new timestamp
    useEffect(() => {
        if (!hasShowroomContent) return undefined;
        if (!isAtTargetTimestamp) return;
        setShowChapterName(true);
        const t = setTimeout(() => setShowChapterName(false), 4000);
        return () => clearTimeout(t);
    }, [isAtTargetTimestamp, currentTimestampIndex, hasShowroomContent]);

    // HEAD request to get video file size for KB/s calculation (same-origin or CORS-permissive server)
    useEffect(() => {
        if (!VIDEO_DATA?.uri) return;
        videoFileSizeRef.current = 0;
        fetch(VIDEO_DATA.uri, { method: 'HEAD' })
            .then(r => {
                const cl = r.headers.get('content-length');
                if (cl) videoFileSizeRef.current = parseInt(cl, 10);
            })
            .catch(() => {});
    }, [VIDEO_DATA?.uri]); // eslint-disable-line react-hooks/exhaustive-deps

    // RAF loop: read video.buffered every frame → update bar DOM + compute KB/s from buffered growth
    useEffect(() => {
        if (!hasShowroomContent) return;
        let rafId;
        let prevBufferedEnd = 0;
        let prevBytesLoaded = 0;
        let prevTime = 0;

        // PerformanceObserver tracks actual bytes downloaded even before duration is known
        let observer = null;
        try {
            observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    if (entry.initiatorType === 'video' || entry.name?.includes('showroom')) {
                        if (entry.transferSize > 0) prevBytesLoaded = entry.transferSize;
                    }
                }
            });
            observer.observe({ type: 'resource', buffered: true });
        } catch (_) {}

        const update = () => {
            const video = videoRef.current;
            if (video) {
                const duration = video.duration;
                const durationKnown = duration > 0 && isFinite(duration);

                if (durationKnown && video.buffered.length > 0) {
                    const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                    const pct = Math.min(100, Math.round((bufferedEnd / duration) * 100));
                    if (bufferBarRef.current) bufferBarRef.current.style.width = `${pct}%`;
                    if (bufferPctTextRef.current) bufferPctTextRef.current.textContent = `${pct}%`;
                    if (pct >= 100) setVideoFullyLoaded(true);

                    const totalBytes = videoFileSizeRef.current;
                    if (totalBytes > 0) {
                        const now = performance.now();
                        const deltaT = (now - prevTime) / 1000;
                        const deltaBuffered = bufferedEnd - prevBufferedEnd;
                        if (prevTime > 0 && deltaT >= 0.75) {
                            if (deltaBuffered > 0) {
                                const deltaBytes = (deltaBuffered / duration) * totalBytes;
                                const kbps = Math.round((deltaBytes / deltaT) / 1024);
                                if (kbps > 0) setBufferSpeed(kbps >= 1024 ? `${(kbps / 1024).toFixed(1)} MB/s` : `${kbps} KB/s`);
                            }
                            prevBufferedEnd = bufferedEnd;
                            prevTime = now;
                        } else if (prevTime === 0) {
                            prevBufferedEnd = bufferedEnd;
                            prevTime = now;
                        }
                    }
                } else if (!durationKnown) {
                    // Moov atom not yet loaded (video not faststart-encoded).
                    // Show animated dots so the user knows it's downloading.
                    if (bufferPctTextRef.current) {
                        const dots = '.'.repeat((Math.floor(performance.now() / 500) % 3) + 1);
                        bufferPctTextRef.current.textContent = `Downloading${dots}`;
                    }
                    // Estimate speed from PerformanceObserver bytes if available
                    if (prevBytesLoaded > 0) {
                        const now = performance.now();
                        const deltaT = (now - prevTime) / 1000;
                        if (prevTime > 0 && deltaT >= 0.75) {
                            const deltaBytes = prevBytesLoaded - prevBufferedEnd;
                            if (deltaBytes > 0) {
                                const kbps = Math.round((deltaBytes / deltaT) / 1024);
                                if (kbps > 0) setBufferSpeed(kbps >= 1024 ? `${(kbps / 1024).toFixed(1)} MB/s` : `${kbps} KB/s`);
                            }
                            prevBufferedEnd = prevBytesLoaded;
                            prevTime = now;
                        } else if (prevTime === 0) {
                            prevBufferedEnd = prevBytesLoaded;
                            prevTime = now;
                        }
                    }
                }
            }
            rafId = requestAnimationFrame(update);
        };
        rafId = requestAnimationFrame(update);
        return () => {
            cancelAnimationFrame(rafId);
            observer?.disconnect();
        };
    }, [hasShowroomContent]);

    // Pause video while cart or drawer is open; resume when both close
    useEffect(() => {
        if (!hasShowroomContent || !videoRef.current) return;
        const video = videoRef.current;
        if (experienceCartOpen || drawerOpen) {
            if (!video.paused) {
                video.pause();
            }
        } else {
            // Only resume if we were mid-travel (not sitting at a chapter)
            if (video.paused && !isAtTargetTimestamp && videoInitialized) {
                video.play().catch(() => {});
            }
        }
    }, [experienceCartOpen, drawerOpen, hasShowroomContent, isAtTargetTimestamp, videoInitialized]);

    // 3D parallax tilt loop - runs entirely via refs, no React re-renders per frame
    const startTiltLoop = useCallback(() => {
        if (tiltRafRef.current) return;
        const loop = () => {
            const t = tiltRef.current;
            t.x += (t.targetX - t.x) * 0.02;
            t.y += (t.targetY - t.y) * 0.02;
            if (tiltWrapperRef.current) {
                tiltWrapperRef.current.style.transform = `perspective(900px) rotateX(${t.y}deg) rotateY(${t.x}deg)`;
            }
            const done = Math.abs(t.targetX - t.x) < 0.01 && Math.abs(t.targetY - t.y) < 0.01;
            if (!done) {
                tiltRafRef.current = requestAnimationFrame(loop);
            } else {
                t.x = t.targetX; t.y = t.targetY;
                if (tiltWrapperRef.current) {
                    tiltWrapperRef.current.style.transform = `perspective(900px) rotateX(${t.y}deg) rotateY(${t.x}deg)`;
                }
                tiltRafRef.current = null;
            }
        };
        tiltRafRef.current = requestAnimationFrame(loop);
    }, []);

    // Keep ref in sync and reset tilt when leaving a chapter pause
    useEffect(() => {
        isAtTargetTimestampRef.current = isAtTargetTimestamp;
        if (!isAtTargetTimestamp) {
            tiltRef.current.targetX = 0;
            tiltRef.current.targetY = 0;
            startTiltLoop();
        }
    }, [isAtTargetTimestamp, startTiltLoop]);

    const renderVideoBanner = () => {
        const currentTimestamp = VIDEO_DATA.timestamps[currentTimestampIndex];

        const handleTiltMove = (e) => {
            if (!isAtTargetTimestampRef.current) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const nx = (e.clientX - rect.left) / rect.width * 2 - 1;   // -1 to +1
            const ny = (e.clientY - rect.top)  / rect.height * 2 - 1;  // -1 to +1
            tiltRef.current.targetX = nx * 4;    // ±4° yaw
            tiltRef.current.targetY = -ny * 2.5; // ±2.5° pitch (inverted)
            startTiltLoop();
        };

        const handleTiltLeave = () => {
            tiltRef.current.targetX = 0;
            tiltRef.current.targetY = 0;
            startTiltLoop();
        };
        
        const handleContainerClick = (e) => {
            // Ignore clicks that originated from a dot or card
            if (e.target.closest('.Dot') || e.target.closest('.FloatingCard')) return;
            // Only act while video is playing - jump to next checkpoint
            if (videoRef.current && !videoRef.current.paused) {
                nextTimestampRef.current?.();
            }
        };

        return (
            <div className="VideoBannerContainer" onClick={handleContainerClick} onMouseMove={handleTiltMove} onMouseLeave={handleTiltLeave}>
                <div ref={tiltWrapperRef} className="VideoBannerTiltWrapper">
                    <video 
                        ref={videoRef}
                        src={VIDEO_DATA.uri}
                        muted
                        playsInline
                        preload="auto"
                        fetchPriority="high"
                        className="VideoBannerVideo"
                        onLoadStart={() => {
                            console.log('[Video] loadstart — src:', VIDEO_DATA.uri, '| readyState:', videoRef.current?.readyState);
                            setBufferSpeed('');
                            setVideoFullyLoaded(false);
                            bufferSpeedRef.current = { prevBytes: 0, prevTime: 0 };
                            if (bufferBarRef.current) bufferBarRef.current.style.width = '0%';
                            if (bufferPctTextRef.current) bufferPctTextRef.current.textContent = '0%';
                            if (bufferingTimerRef.current) clearTimeout(bufferingTimerRef.current);
                            bufferingTimerRef.current = setTimeout(() => {
                                console.log('[Video] buffering timeout hit → setIsBuffering(true) | isAtTargetTimestamp:', isAtTargetTimestamp);
                                setIsBuffering(true);
                            }, 400);
                        }}
                        onWaiting={() => {
                            console.log('[Video] waiting (network stall) | readyState:', videoRef.current?.readyState, '| isAtTargetTimestamp:', isAtTargetTimestamp);
                            if (bufferingTimerRef.current) clearTimeout(bufferingTimerRef.current);
                            bufferingTimerRef.current = setTimeout(() => {
                                console.log('[Video] buffering timeout hit (waiting) → setIsBuffering(true)');
                                setIsBuffering(true);
                            }, 400);
                        }}
                        onPlaying={() => {
                            console.log('[Video] playing → setIsBuffering(false)');
                            if (bufferingTimerRef.current) { clearTimeout(bufferingTimerRef.current); bufferingTimerRef.current = null; }
                            setIsBuffering(false);
                        }}
                        onCanPlay={() => {
                            console.log('[Video] canplay → setIsBuffering(false) | readyState:', videoRef.current?.readyState);
                            if (bufferingTimerRef.current) { clearTimeout(bufferingTimerRef.current); bufferingTimerRef.current = null; }
                            setIsBuffering(false);
                        }}
                        onCanPlayThrough={() => {
                            console.log('[Video] canplaythrough');
                        }}
                        onProgress={(e) => {
                            const video = e.currentTarget;
                            if (video.buffered.length > 0 && video.duration > 0) {
                                const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                                const pct = Math.round((bufferedEnd / video.duration) * 100);
                                console.log(`[Video] progress — buffered: ${pct}% (${bufferedEnd.toFixed(1)}s / ${video.duration.toFixed(1)}s)`);
                            }
                        }}
                    />
                    {/* Container that matches the actual video dimensions */}
                    <div className="VideoDotsContainer">
                        {showDots && isAtTargetTimestamp && videoRef.current?.paused && dotPixelPositions.chapterIndex === currentTimestampIndex && currentTimestamp?.dots && currentTimestamp.dots.map((dot, dotIndex) => {
                            const isSelected = selectedDot === dot;
                            const pos = dotPixelPositions.positions[dotIndex];
                            if (!pos) return null;

                            return (
                                <div 
                                    className={`Dot VideoDot ${isSelected ? 'selected' : ''} visible`}
                                    key={dotIndex} 
                                    style={{
                                        left: pos.x + 'px',
                                        top: pos.y + 'px'
                                    }}
                                    onClick={(e) => handleDotClick(dot, e)}
                                >
                                    <div className="DotIndicator"><span className="DotPlus">+</span></div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                {(isBuffering && !isAtTargetTimestamp) || (!videoInitialized) ? (
                    <div className="VideoStatusLabel">
                        <div className="VideoStatusSpinner" />
                        {isBuffering && !isAtTargetTimestamp ? 'Streaming' : 'Loading'}
                    </div>
                ) : null}
                {!videoInitialized && (
                    <div className="VideoInitLoader">
                        <div className="VideoInitSpinner" />
                    </div>
                )}
                {!videoFullyLoaded && (
                    <div className="VideoLoadProgressWrap">
                        <div className="VideoLoadBar">
                            <div className="VideoLoadFill" ref={bufferBarRef} style={{ width: '0%' }} />
                        </div>
                        <div className="VideoLoadMeta">
                            <span ref={bufferPctTextRef}>0%</span>
                            {bufferSpeed && <><span className="VideoLoadSep">·</span><span>{bufferSpeed}</span></>}
                        </div>
                    </div>
                )}
                {/* Video overlay with center mask */}
                {showOverlay && (
                    <div className={`VideoOverlay ${overlayFading ? 'fading' : ''}`}></div>
                )}
                {selectedDot && (
                    <div 
                        className={`FloatingCard ${cardPosition.side}`}
                        style={{
                            left: cardPosition.x + 'px',
                            top: cardPosition.y + 'px'
                        }}
                    >
                        {/* Connecting line from dot to card */}
                        <div className={`ConnectingLine ${cardPosition.side}`}></div>
                        <button className="CloseButton" onClick={closePopup}>×</button>
                        <div className="CardContent">
                            <div className="CardInfo">
                                <div className="CardHeader">
                                    <h2>{selectedDot.item.title}</h2>
                                    <span className="CardSubtitle">{selectedDot.item.subtitle}</span>
                                    <div className="PriceSection">
                                        <span className="CurrentPrice">{selectedDot.item.price}</span>
                                        {selectedDot.item.originalPrice && (
                                            <span className="OriginalPrice">{selectedDot.item.originalPrice}</span>
                                        )}
                                    </div>
                                </div>
                                <p className="Description">{selectedDot.item.text}</p>
                                <div className="Features">
                                    <h4>Key Features:</h4>
                                    <ul>
                                        {selectedDot.item.features.map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="CardActions">
                                    <div className="StockStatus">
                                        <span className={`Stock ${selectedDot.item.inStock ? 'inStock' : 'outOfStock'}`}>
                                            {selectedDot.item.inStock ? '✓ In Stock' : '⏳ Pre-Order'}
                                        </span>
                                    </div>
                                        <div className="CardActionsRow">
                                        <button
                                            className={`WishlistBtn ${isWishlisted(selectedDot.item) ? 'active' : ''}`}
                                            onClick={() => toggleWishlist(selectedDot.item)}
                                            title={isWishlisted(selectedDot.item) ? 'Remove from wishlist' : 'Save to wishlist'}
                                        >
                                            {isWishlisted(selectedDot.item)
                                                ? <RiHeartFill size={16} />
                                                : <RiHeartLine size={16} />}
                                        </button>
                                        {selectedDot.item.productId ? (
                                            <button
                                                className="ViewProductBtn"
                                                onClick={() => openProductDrawer(selectedDot.item.productId)}
                                            >
                                                View Product
                                            </button>
                                        ) : (
                                            <button className="AddToCartBtn">
                                                {selectedDot.item.inStock ? 'Order' : 'Pre-Order Now'}
                                            </button>
                                        )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                {/* Chapter name toast */}
                {showChapterName && (
                    <div className="ChapterNameToast">
                        <span className="ChapterNameRoom">Room {currentTimestampIndex + 1} of {VIDEO_DATA.timestamps.length}</span>
                        <span className="ChapterNameLabel">{CHAPTER_LABELS[currentTimestampIndex]}</span>
                    </div>
                )}

                {/* Auto-advance progress bar - only once video is loaded and playing */}
            </div>
        );
    };

    const getMainView = () => {
        if (sectionsLoading) {
            return (
                <div className="ExperienceStateScreen">
                    <div className="ExperienceStateCard">
                        <RiLoader4Line className="ExperienceStateSpinner" size={26} />
                        <h2>Loading showroom</h2>
                        <p>Pulling live showroom content for the experience.</p>
                    </div>
                </div>
            );
        }

        if (!hasShowroomContent) {
            return (
                <div className="ExperienceStateScreen" style={{ flexDirection: 'column' }}>
                    {sections.length > 1 && (
                        <div className="SectionTabs">
                            {sections.map((section, i) => (
                                <button
                                    key={section.id}
                                    className={`SectionTab ${i === currentSectionIndex ? 'active' : ''}`}
                                    onClick={() => switchSection(i)}
                                >
                                    {section.name}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="ExperienceStateCard">
                        <h2>Showroom unavailable</h2>
                        <p>{sectionsError || 'No production showroom content is published yet.'}</p>
                        <button className="ExperienceStateBtn" onClick={() => exitExperience()}>
                            Go to home
                        </button>
                    </div>
                </div>
            );
        }


        // if(!account?.admin) return;

        // Skipper stays on the right; only dodges up or down to avoid dots
        const containerHeight = videoRef.current?.parentElement?.clientHeight || window.innerHeight;
        const skipperW = skipperRef.current?.offsetWidth || 320;
        const skipperH = skipperRef.current?.offsetHeight || 200;
        const PAD = 24;

        // Pick top-right vs bottom-right — whichever overlaps fewer dots
        let skipperAtBottom = false;
        if (isAtTargetTimestamp && dotPixelPositions.positions.length > 0) {
            const rightEdge = window.innerWidth - PAD;
            const leftEdge  = rightEdge - skipperW;
            const topY      = PAD;
            const botY      = containerHeight - skipperH - PAD;
            const overlapTop = dotPixelPositions.positions.filter(p =>
                p.x >= leftEdge - 16 && p.x <= rightEdge + 16 &&
                p.y >= topY - 16    && p.y <= topY + skipperH + 16
            ).length;
            const overlapBot = dotPixelPositions.positions.filter(p =>
                p.x >= leftEdge - 16 && p.x <= rightEdge + 16 &&
                p.y >= botY - 16     && p.y <= botY + skipperH + 16
            ).length;
            skipperAtBottom = overlapBot < overlapTop;
        }

        return (
            <div className={`VideoBannerContent${sectionFading ? ' section-fading' : ''}`}>

                <div
                    ref={skipperRef}
                    className={`VideoBannerSkipper${isAtTargetTimestamp ? '' : ' skipper--travelling'}${isAtTargetTimestamp && skipperAtBottom ? ' skipper--bottom' : ''}`}>
                    <div className="VideoBannerSkipperBrand">
                        <img src={bdsLogoLight} alt="BDS" className="VideoBannerSkipperLogo" />
                        {isAtTargetTimestamp && (
                            <button
                                className={`AutoAdvanceToggle${autoAdvanceEnabled ? ' on' : ' off'}`}
                                onClick={() => setAutoAdvanceEnabled(v => !v)}
                                data-tooltip-id="experience-tooltip"
                                data-tooltip-place="bottom"
                                data-tooltip-content={autoAdvanceEnabled ? 'Disable autoplay' : 'Enable autoplay'}
                            >
                                {autoAdvanceEnabled ? <RiTimerLine size={15} /> : <RiTimerFlashLine size={15} />}
                            </button>
                        )}
                    </div>

                    <div className="ChapterSteps">
                        {VIDEO_DATA.timestamps.map((_, i) => {
                            const pills = [];
                            const isChapterActive = isAtTargetTimestamp && i === currentTimestampIndex;
                            const isChapterVisited = i < currentTimestampIndex;
                            pills.push(
                                <button
                                    key={`ch-${i}`}
                                    className={`ChapterStep${isChapterActive ? ' active' : ''}${isChapterVisited ? ' visited' : ''}`}
                                    onClick={() => jumpToTimestamp(i)}
                                    title={CHAPTER_LABELS[i] || `Room ${i + 1}`}
                                >
                                    <span className="ChapterStepNum">{i + 1}</span>
                                </button>
                            );
                            if (i < VIDEO_DATA.timestamps.length - 1) {
                                const isTravelActive = !isAtTargetTimestamp && travelSegmentIndex === i;
                                const isTravelVisited = isAtTargetTimestamp
                                    ? i < currentTimestampIndex
                                    : travelSegmentIndex !== null && i < travelSegmentIndex;
                                pills.push(
                                    <div
                                        key={`tr-${i}`}
                                        className={`ChapterStep ChapterStep--travel${isTravelActive ? ' active' : ''}${isTravelVisited ? ' visited' : ''}`}
                                    />
                                );
                            }
                            return pills;
                        })}
                    </div>

                    {!isAtTargetTimestamp && travelSegmentIndex !== null && (
                        <div className="TravelProgressBarWrapper">
                            <div className="TravelProgressBar" ref={progressBarRef} style={{ width: '0%' }} />
                        </div>
                    )}

                    {isAtTargetTimestamp && (
                    <div className="VideoBannerSkipperOptions">
                        <button 
                            className="VideoBannerSkipperOptionsArrow Left" 
                            onClick={prevTimestamp}
                            disabled={currentTimestampIndex === 0}
                            style={{
                                opacity: currentTimestampIndex === 0 ? 0.3 : 1
                            }}
                        >
                            <RiArrowLeftSLine  />
                            <span className="VideoBannerSkipperOptionsArrowText">Previous</span>
                        </button>

                        {(() => {
                            const isLastChapter = currentTimestampIndex === VIDEO_DATA.timestamps.length - 1;
                            const isLastSection = currentSectionIndex === sections.length - 1;
                            const videoPlaying = videoRef.current && !videoRef.current.paused;

                            if (isAtTargetTimestamp && isLastChapter && isLastSection) {
                                // End of everything - show Close CTA
                                return (
                                    <button
                                        className="VideoBannerSkipperOptionsArrow Right EndCTA"
                                        onClick={() => exitExperience()}
                                    >
                                        <span className="VideoBannerSkipperOptionsArrowText">Close</span>
                                        <RiCloseLine />
                                    </button>
                                );
                            }
                            if (isAtTargetTimestamp && isLastChapter && !isLastSection) {
                                // End of this section - show Next Section
                                return (
                                    <button
                                        className="VideoBannerSkipperOptionsArrow Right NextSection"
                                        onClick={() => switchSection(currentSectionIndex + 1)}
                                    >
                                        <span className="VideoBannerSkipperOptionsArrowText">Next Section</span>
                                        <RiArrowRightSLine />
                                    </button>
                                );
                            }
                            if (!isLastChapter || videoPlaying) {
                                return (
                                    <button
                                        className="VideoBannerSkipperOptionsArrow Right"
                                        onClick={nextTimestamp}
                                    >
                                        <span className="VideoBannerSkipperOptionsArrowText">
                                            {videoPlaying ? 'Skip' : 'Next'}
                                        </span>
                                        <RiArrowRightSLine />
                                    </button>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    )}

                    {autoAdvanceEnabled && isAtTargetTimestamp && videoInitialized && !selectedDot && !isGuideActive && !experienceCartOpen && !drawerOpen && currentTimestampIndex < VIDEO_DATA.timestamps.length - 1 && (
                        <div className="AutoAdvanceBarWrapper">
                            <div
                                className="AutoAdvanceBar"
                                key={`${currentTimestampIndex}-${isAtTargetTimestamp}-${experienceCartOpen}-${drawerOpen}`}
                            />
                        </div>
                    )}
                </div>

                {/* ── Shop this scene strip ── */}
                {isAtTargetTimestamp && (() => {
                    const dots = VIDEO_DATA.timestamps[currentTimestampIndex]?.dots;
                    if (!dots?.length) return null;
                    return (
                        <div className={`SceneShopStrip ${experienceCartItems.length > 0 ? 'Cart' : ''}`}>
                            <span className="SceneShopLabel">In this scene</span>
                            <div className="SceneShopItems">
                                {dots.map((dot, i) => (
                                    <button
                                        key={`${dot.item?.productId ?? 'dot'}-${i}`}
                                        className="SceneShopItem"
                                        onClick={() => openProductDrawer(dot.item?.productId)}
                                    >
                                        {dot.item?.image
                                            ? <img src={dot.item.image} alt={dot.item.title} onError={e => e.target.style.display='none'} />
                                            : <div className="SceneShopItemImagePlaceholder" />
                                        }
                                        <span className="SceneShopItemName">{dot.item?.title}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                <button
                    className={`BannerArrow Left${currentTimestampIndex === 0 ? ' invisible' : ''}`}
                    onClick={prevTimestamp}
                    disabled={currentTimestampIndex === 0}
                >
                    <RiArrowLeftSLine  />
                </button>

                <div className="SectionTabs">
                    {sections.map((section, i) => (
                        <button
                            key={section.id}
                            className={`SectionTab ${i === currentSectionIndex ? 'active' : ''}`}
                            onClick={() => switchSection(i)}
                        >
                            {section.name}
                        </button>
                    ))}
                </div>

                <div className="VideoBanner">
                    {renderVideoBanner()}
                </div>

                <button
                    className={`BannerArrow Right${
                        currentTimestampIndex < VIDEO_DATA.timestamps.length - 1 ||
                        (currentTimestampIndex === VIDEO_DATA.timestamps.length - 1 && videoRef.current && !videoRef.current.paused)
                            ? '' : ' invisible'
                    }`}
                    onClick={nextTimestamp}
                    disabled={
                        !(currentTimestampIndex < VIDEO_DATA.timestamps.length - 1 ||
                        (currentTimestampIndex === VIDEO_DATA.timestamps.length - 1 && videoRef.current && !videoRef.current.paused))
                    }
                >
                    <RiArrowRightSLine  />
                </button>
            </div>
        );
    };

    return (
        <>
        {isPortrait && (
            <div className="RotateOverlay">
                <div className="RotateOverlayIcon">⟳</div>
                <div className="RotateOverlayTitle">Rotate your device</div>
                <div className="RotateOverlayText">For the best experience, hold your phone in landscape mode.</div>
                <button className="RotateOverlayBack" onClick={() => exitExperience()}>← Go back</button>
            </div>
        )}
        <div className="Dash">
            <button className="ExperienceExitBtn" onClick={() => exitExperience()} title="Go back">
                <RiArrowLeftLine size={15} />
                Back
            </button>

           { getMainView() }

            {/* ── Product Drawer ─────────────────────────────────────────────── */}
            {drawerOpen && (
                <div className="ProductDrawerBackdrop" onClick={closeProductDrawer} />
            )}
            <div className={`ProductDrawerPanel${drawerOpen ? ' open' : ''}`}>
                <button className="ProductDrawerClose" onClick={closeProductDrawer} title="Close">
                    <RiCloseLine size={22} />
                </button>

                {drawerLoading && (
                    <div className="ProductDrawerLoading">
                        <span>Loading product…</span>
                    </div>
                )}

                {!drawerLoading && !drawerProduct && drawerOpen && (
                    <div className="ProductDrawerLoading">
                        <span>Product not found.</span>
                    </div>
                )}

                {!drawerLoading && drawerProduct && (() => {
                    const tp = getDrawerTransformed();
                    return (
                        <div className="ProductDrawerContent">
                            {/* Images */}
                            <div className="ProductDrawerImages">
                                <div className="ProductDrawerMainImage">
                                    <img
                                        src={tp.images[drawerImageIndex]?.url || undefined}
                                        alt={tp.images[drawerImageIndex]?.alt || tp.name}
                                        onError={e => e.target.removeAttribute('src')}
                                    />
                                    {tp.images.length > 1 && (
                                        <div className="ProductDrawerImageDots">
                                            {tp.images.map((_, i) => (
                                                <button
                                                    key={i}
                                                    className={`ProductDrawerImageDot${i === drawerImageIndex ? ' active' : ''}`}
                                                    onClick={() => setDrawerImageIndex(i)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {tp.images.length > 1 && (
                                    <div className="ProductDrawerThumbs">
                                        {tp.images.map((img, i) => (
                                            <button
                                                key={img.id}
                                                className={`ProductDrawerThumb${i === drawerImageIndex ? ' active' : ''}`}
                                                onClick={() => setDrawerImageIndex(i)}
                                            >
                                                <img src={img.url || undefined} alt={img.alt} onError={e => e.target.removeAttribute('src')} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Details */}
                            <div className="ProductDrawerDetails">
                                {tp.brand && <div className="ProductDrawerBrand">{tp.brand}</div>}
                                <h2 className="ProductDrawerTitle">{tp.name}</h2>
                                <p className="ProductDrawerShortDesc">{tp.shortDescription}</p>

                                {/* Pricing */}
                                <div className="ProductDrawerPricing">
                                    {tp.comingSoon ? (
                                        <span className="ProductDrawerComingSoon">Coming Soon</span>
                                    ) : (
                                        <>
                                            <span className="ProductDrawerPrice">
                                                {tp.price.curr} {calculateDrawerTotal(tp).toFixed(2)}
                                            </span>
                                            {tp.price.originalAmount && (
                                                <span className="ProductDrawerOriginalPrice">
                                                    {tp.price.curr}{(tp.price.originalAmount * drawerQuantity).toFixed(2)}
                                                </span>
                                            )}
                                            {tp.price.discount > 0 && (
                                                <span className="ProductDrawerDiscount">{tp.price.discount}% OFF</span>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Stock */}
                                <div className="ProductDrawerStock">
                                    {tp.inStock ? (
                                        <span className="ProductDrawerInStock">
                                            <RiCheckLine size={14} /> In Stock
                                            {tp.stockCount !== 999 && tp.stockCount > 0 ? ` (${tp.stockCount} available)` : null}
                                        </span>
                                    ) : (
                                        <span className="ProductDrawerOutStock">
                                            <RiErrorWarningLine size={14} /> Out of Stock
                                        </span>
                                    )}
                                </div>

                                {/* Modifiers */}
                                {tp.modifiers.length > 0 && (
                                    <div className="ProductDrawerModifiers">
                                        {tp.modifiers.map(mod => (
                                            <div key={mod.id} className="ProductDrawerModGroup">
                                                <label className="ProductDrawerModLabel">
                                                    {mod.name}
                                                    {mod.required && <span style={{ color: '#dc2626' }}> *</span>}
                                                </label>
                                                {mod.id === 'color' ? (
                                                    <div className="ProductDrawerColorOptions">
                                                        {mod.options.map(opt => (
                                                            <button
                                                                key={opt.id}
                                                                className={`ProductDrawerColorOpt${drawerModifiers[mod.id]?.id === opt.id ? ' selected' : ''}${!opt.inStock ? ' out-of-stock' : ''}`}
                                                                onClick={() => opt.inStock && setDrawerModifiers(prev => ({ ...prev, [mod.id]: opt }))}
                                                                disabled={!opt.inStock}
                                                            >
                                                                <div className="color-swatch" style={{ backgroundColor: opt.color }} />
                                                                <span>{opt.name}{opt.amount > 0 ? ` - $${opt.amount}` : ''}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <select
                                                        className="ProductDrawerSelect"
                                                        value={drawerModifiers[mod.id]?.id || ''}
                                                        onChange={e => {
                                                            const opt = mod.options.find(o => o.id === e.target.value);
                                                            if (opt) setDrawerModifiers(prev => ({ ...prev, [mod.id]: opt }));
                                                        }}
                                                    >
                                                        <option value="">Select {mod.name}</option>
                                                        {mod.options.map(opt => (
                                                            <option key={opt.id} value={opt.id}>
                                                                {opt.name}
                                                                {opt.description ? ` - ${opt.description}` : ''}
                                                                {opt.amount > 0 ? ` - $${opt.amount}` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Quantity */}
                                <div className="ProductDrawerQuantity">
                                    <span className="ProductDrawerQuantityLabel">Quantity</span>
                                    <div className="ProductDrawerQuantityControls">
                                        <button
                                            onClick={() => setDrawerQuantity(q => Math.max(1, q - 1))}
                                            disabled={drawerQuantity <= 1}
                                        >
                                            <RiSubtractLine />
                                        </button>
                                        <span className="ProductDrawerQuantityValue">{drawerQuantity}</span>
                                        <button
                                            onClick={() => setDrawerQuantity(q => Math.min(tp.stockCount || 99, q + 1))}
                                            disabled={tp.stockCount !== 999 && drawerQuantity >= tp.stockCount}
                                        >
                                            <RiAddLine />
                                        </button>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="ProductDrawerActions">
                                    {drawerAddedToCart ? (
                                        <div className="ProductDrawerPostAddActions">
                                            <button
                                                className="ProductDrawerSecondaryAction"
                                                onClick={openExperienceCart}
                                            >
                                                View cart
                                            </button>
                                            <button
                                                className="ProductDrawerAddToCart checkout"
                                                onClick={() => navigate(account ? '/checkout' : '/sign-in?ref=cart')}
                                            >
                                                Checkout
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="ProductDrawerAddToCart"
                                            onClick={() => handleDrawerAddToCart(tp)}
                                            disabled={tp.comingSoon || !tp.inStock || drawerAddingToCart}
                                        >
                                            <RiShoppingCartLine size={16} />
                                            {tp.comingSoon ? 'Coming Soon' :
                                             drawerAddingToCart ? 'Adding…' :
                                             `Add to Cart - ${tp.price.curr} ${calculateDrawerTotal(tp).toFixed(2)}`}
                                        </button>
                                    )}
                                    <button
                                        className={`ProductDrawerWishlist${drawerWishlisted ? ' active' : ''}`}
                                        onClick={() => handleDrawerWishlistToggle(tp)}
                                        disabled={drawerTogglingWishlist}
                                        title={drawerWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                                    >
                                        {drawerWishlisted ? <RiHeart3Fill size={18} /> : <RiHeart3Line size={18} />}
                                    </button>
                                </div>

                                {/* Description */}
                                {tp.description && (
                                    <div className="ProductDrawerDescription">
                                        <h4>Description</h4>
                                        <p>{tp.description}</p>
                                    </div>
                                )}

                                {/* Features */}
                                {tp.features.length > 0 && (
                                    <div className="ProductDrawerFeatures">
                                        <h4>Key Features</h4>
                                        <ul>
                                            {tp.features.map((f, i) => <li key={i}>{f}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </div>

            {experienceCartItems.length > 0 && (
                <>
                    {experienceCartOpen && <div className="ExperienceCartBackdrop" onClick={() => setExperienceCartOpen(false)} />}

                    <div className={`ExperienceCartPanel${experienceCartOpen ? ' open' : ''}`}>
                        <div className="ExperienceCartPanelHeader">
                            <div>
                                <h4>Your cart</h4>
                                <span>{experienceCartItems.length} item{experienceCartItems.length === 1 ? '' : 's'}</span>
                            </div>
                            <button className="ExperienceCartPanelClose" onClick={() => setExperienceCartOpen(false)} title="Close cart">
                                <RiCloseLine size={18} />
                            </button>
                        </div>

                        {experienceCartLoading ? (
                            <div className="ExperienceDrawerCartLoading">
                                <RiLoader4Line className="ExperienceDrawerSpin" size={18} />
                                <span>Loading cart…</span>
                            </div>
                        ) : (
                            <>
                                <div className="ExperienceDrawerCartItems">
                                    {experienceCartItems.map((item) => {
                                        const itemBusy = Boolean(experienceCartUpdating[item.cart_id]);
                                        return (
                                            <div className="ExperienceDrawerCartItem" key={item.cart_id}>
                                                {item.image && <img className="ExperienceDrawerCartItemImage" src={item.image} alt={item.name} onError={e => e.target.style.display = 'none'} />}
                                                <div className="ExperienceDrawerCartItemBody">
                                                    <div className="ExperienceDrawerCartItemTop">
                                                        <div>
                                                            <div className="ExperienceDrawerCartItemName">{item.name}</div>
                                                            <div className="ExperienceDrawerCartItemPrice">
                                                                {item.price_curr} {getDrawerCartItemTotal(item).toFixed(2)}
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="ExperienceDrawerCartRemove"
                                                            onClick={() => removeDrawerCartItem(item.cart_id)}
                                                            disabled={itemBusy}
                                                            title="Remove item"
                                                        >
                                                            <RiDeleteBin6Line size={16} />
                                                        </button>
                                                    </div>

                                                    <div className="ExperienceDrawerCartQty">
                                                        <button
                                                            onClick={() => updateDrawerCartQuantity(item.cart_id, (parseInt(item.qty, 10) || 1) - 1)}
                                                            disabled={itemBusy || parseInt(item.qty, 10) <= 1}
                                                        >
                                                            <RiSubtractLine size={14} />
                                                        </button>
                                                        <span>{item.qty}</span>
                                                        <button
                                                            onClick={() => updateDrawerCartQuantity(item.cart_id, (parseInt(item.qty, 10) || 0) + 1)}
                                                            disabled={itemBusy}
                                                        >
                                                            <RiAddLine size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="ExperienceDrawerCartSummary">
                                    <div>
                                        <span>Total</span>
                                        <strong>{getDrawerCartCurrency()} {getDrawerCartTotal().toFixed(2)}</strong>
                                    </div>
                                    <button
                                        className="ExperienceDrawerCheckoutBtn full"
                                        onClick={() => navigate(account ? '/checkout' : '/sign-in?ref=cart')}
                                        disabled={experienceCartItems.length === 0}
                                    >
                                        Checkout
                                        <RiArrowRightLine size={16} />
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <button
                        className={`ExperienceFloatingCart${experienceCartOpen ? ' open' : ''}`}
                        onClick={toggleExperienceCart}
                    >
                        <RiShoppingCartLine size={18} />
                        <span>View cart</span>
                        <span className="ExperienceFloatingCartCount">{experienceCartItems.length}</span>
                    </button>
                </>
            )}

        </div>
        <Tooltip id="experience-tooltip" />

        {/* ── Onboarding Guide ───────────────────────────────────────────── */}
        {isGuideActive && (() => {
            const step = GUIDE_STEPS[guideStep];
            const isLast = guideStep === GUIDE_STEPS.length - 1;

            // For the 'dots' step anchor card + spotlight on the first actual dot
            let cardStyle = null;
            let spotStyle = null;
            let tipDir = step.tip;
            let anchorClass = step.anchor;

            if (step.id === 'dots' && dotGuidePos) {
                // Position is captured by useEffect after the dots are painted -
                // so the card is correct on the very first render with no flicker.
                const { vx, vy } = dotGuidePos;
                const cardW = 280;
                const left = Math.min(Math.max(vx - cardW / 2, 16), window.innerWidth - cardW - 16);
                if (vy < window.innerHeight * 0.55) {
                    cardStyle = { top: vy + 52, left, transform: 'none' };
                    tipDir = 'top';
                } else {
                    cardStyle = { top: Math.max(vy - 260, 16), left, transform: 'none' };
                    tipDir = 'bottom';
                }
                spotStyle = { left: vx - 36, top: vy - 36, width: 72, height: 72, borderRadius: '50%', transform: 'none' };
                anchorClass = null;
            }

            if (step.id === 'strip' && stripGuidePos) {
                const { height: sh } = stripGuidePos;
                const spotLeft = 12;
                const spotWidth = 420;
                const spotTop = window.innerHeight - 12 - sh;
                const cardW = 280;
                const cardLeft = Math.min(Math.max(spotLeft, 16), window.innerWidth - cardW - 16);
                cardStyle = { top: spotTop - 16 - 160, left: cardLeft, transform: 'none' };
                tipDir = 'bottom';
                spotStyle = { left: spotLeft, top: spotTop, width: spotWidth, height: sh, borderRadius: 12, transform: 'none' };
                anchorClass = null;
            }

            return (
                <div className="ExpGuideOverlay" onClick={advanceGuide}>
                    <div
                        className={`ExpGuideCard ${anchorClass ? `ExpGuideCard--${anchorClass}` : ''} ExpGuideTip--${tipDir}`}
                        style={cardStyle || undefined}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="ExpGuideCardHeader">
                            <span className="ExpGuideStep">{guideStep + 1} / {GUIDE_STEPS.length}</span>
                            <button className="ExpGuideDismiss" onClick={dismissGuide} title="Skip guide">Skip</button>
                        </div>
                        <div className="ExpGuideIcon">
                            {step.id === 'sections'  && <span>☰</span>}
                            {step.id === 'navigate'  && <span>›</span>}
                            {step.id === 'dots'      && <span>＋</span>}
                            {step.id === 'strip'     && <span>▦</span>}
                        </div>
                        <h4 className="ExpGuideTitle">{step.title}</h4>
                        <p className="ExpGuideBody">{step.body}</p>
                        <button className="ExpGuideNext" onClick={advanceGuide}>
                            {isLast ? 'Got it' : 'Next'}
                        </button>
                    </div>
                    {/* Spotlight highlight region */}
                    <div
                        className={`ExpGuideSpotlight ${anchorClass ? `ExpGuideSpotlight--${anchorClass}` : ''}`}
                        style={spotStyle || undefined}
                    />
                </div>
            );
        })()}

        </>
    );
}
export default Experience;
